import { useEffect, useRef, useState } from 'react';
import { assignFormValues } from '../../../Utils/Auth';
import {
  getICDPanelsIndex,
  panelsArrItemAddChild,
  panelsArrItemRemoval,
  panelsArrMakerToSend,
} from '../../../Utils/Common/Requisition';
import useLang from './../../hooks/useLanguage';
import { useLocation } from 'react-router-dom';

/** Panels with `isVisible === false` must not be included in form payload / defaultValue. */
const panelsVisibleForSend = (panels: any[] | null | undefined) =>
  (panels ?? []).filter((p: any) => p?.isVisible !== false);

/**
* Send payload convention: a panel carries only the tests the user actually kept.
* Deep-copied so the payload never aliases `props.panels` (which drives the checkboxes).
*/
const selectedTestsOnly = (panel: any) => {
  const panelCopy = JSON.parse(JSON.stringify(panel));
  panelCopy.testOptions = (panelCopy.testOptions ?? []).filter(
    (testOption: any) => testOption?.isSelected
  );
  return panelCopy;
};

const panelsSelectedForSend = (panels: any[] | null | undefined) =>
  panelsVisibleForSend(panels).map(selectedTestsOnly);

/**
* Drop one test from the send array.
* Entries are matched by `panelID` — the send array only holds selected panels, so
* an index into `props.panels` points at the wrong entry (or none at all).
*/
const removeTestFromSendArr = (
  panelID: any,
  testID: string,
  sendArr: any[]
) => {
  const sendIndex = sendArr.findIndex(
    (sendPanel: any) => String(sendPanel?.panelID) === String(panelID)
  );
  if (sendIndex === -1) return sendArr;

  const nextSendArr = [...sendArr];
  const panel = { ...nextSendArr[sendIndex] };
  panel.testOptions = (panel.testOptions ?? []).filter(
    (testOption: any) => String(testOption?.testID) !== String(testID)
  );

  // Last test unchecked — the panel itself is no longer ordered.
  if (panel.testOptions.length === 0) {
    nextSendArr.splice(sendIndex, 1);
    return nextSendArr;
  }

  panel.isSelected = true;
  nextSendArr[sendIndex] = panel;
  return nextSendArr;
};

const getSelectedPanelsKey = (panels: any[] = []) =>
  panels
    .filter((p: any) => p?.isSelected && p?.isVisible !== false)
    .map((p: any) => String(p.panelID))
    .sort()
    .join(',');

const isPanelDefaultValueSynced = (field: any, selectedPanels: any[]) => {
  if (!field) return false;
  const selectedKey = getSelectedPanelsKey(selectedPanels);
  const defaultValue = field.defaultValue;
  if (defaultValue === '' || defaultValue == null) {
    return selectedKey === '';
  }
  if (!Array.isArray(defaultValue)) return false;
  const defaultKey = defaultValue
    .map((p: any) => String(p.panelID))
    .sort()
    .join(',');
  return defaultKey === selectedKey;
};

function FindIndex(arr: any[], rid: any) {
  return arr.findIndex((i: any) => i.reqId === rid);
}

const ToxTestingOption = (props: any) => {
  const { t } = useLang();
  const location = useLocation();
  const [panelsArrToSend, setPanelsArrToSend] = useState<any>([]);
  /** Bumps on user actions so stale async setInfectiousData from remount/Continue is ignored. */
  const updateGenerationRef = useRef(0);
  const infectiousDataRef = useRef(props.infectiousData);
  infectiousDataRef.current = props.infectiousData;

  const selectedPanelsKey = getSelectedPanelsKey(props.panels || []);

  const syncInfectiousSections = (updatedSections: any, generation: number) => {
    if (!updatedSections || !props?.setInfectiousData || props?.ArrayReqId == null) {
      return;
    }
    if (generation !== updateGenerationRef.current) return;

    const infectiousData = infectiousDataRef.current;
    const sectionIndex = FindIndex(infectiousData, props.ArrayReqId);
    if (sectionIndex === -1) return;

    const infectiousDataCopy = JSON.parse(JSON.stringify(infectiousData));
    infectiousDataCopy[sectionIndex].sections = updatedSections;
    props.setInfectiousData([...infectiousDataCopy]);
  };

  const applyLivePanelSelection = (
    panelIndex: number,
    selected: boolean,
    testOptionIndex?: number
  ) => {
    const livePanel =
      props.Inputs?.[props.index]?.fields?.[props.fieldIndex]?.panels?.[panelIndex];
    if (!livePanel) return;
    livePanel.isSelected = selected;
    if (typeof testOptionIndex === 'number') {
      if (livePanel.testOptions?.[testOptionIndex]) {
        livePanel.testOptions[testOptionIndex].isSelected = selected;
      }
      return;
    }
    livePanel.testOptions?.forEach((opt: any) => {
      opt.isSelected = selected;
    });
  };

  useEffect(() => {
    const {
      Inputs,
      index,
      fieldIndex,
      depControlIndex,
      isDependency,
      repeatFieldSection,
      isDependencyRepeatFields,
      repeatFieldIndex,
      repeatDependencySectionIndex,
      repeatDepFieldIndex,
      setInputs,
    } = props;

    const currentField = Inputs?.[index]?.fields?.[fieldIndex];
    let panelsToSend: any[] = [];

    if (currentField?.panels && !location.state?.reqId) {
      const panelsCopy = props.panels ? [...props.panels] : [];
      const ICDPanelIndex = getICDPanelsIndex(Inputs);

      currentField.panels.forEach((panel: any, panelIndex: number) => {
        if (panel.isDefaultSelectOnRequisition) {
          panel.isSelected = true;
          panel.testOptions?.forEach((option: any) => {
            option.isSelected = true;
          });
          // Use panelsArrMakerToSend for fully selected panels
          panelsToSend = panelsArrMakerToSend(
            panelIndex,
            panel,
            panelsToSend,
            true
          );
          // Show ICD panel for the selected panel
          Inputs[ICDPanelIndex]?.fields?.forEach((field: any) => {
            if (field?.systemFieldName === 'ICDPanels') {
              field?.panels?.forEach((icdPanel: any) => {
                if (icdPanel?.panelID === panel.panelID) {
                  icdPanel.isVisible = true;
                }
              });
            }
          });
        } else {
          // Check for individual testOptions with isDefaultSelectTestOnRequisition
          let hasDefaultTestOption = false;
          panel.testOptions?.forEach((option: any) => {
            if (option.isDefaultSelectTestOnRequisition) {
              option.isSelected = true;
              hasDefaultTestOption = true;
            }
          });
          // If any testOption is auto-selected, also select the parent panel
          if (hasDefaultTestOption) {
            panel.isSelected = true;
            // Use panelsArrItemAddChild for panels with only some testOptions selected
            panelsToSend = panelsArrItemAddChild(
              panelIndex,
              panel.panelName,
              panel.testOptions.filter((opt: any) => opt.isSelected),
              panelsToSend,
              panelsCopy
            );
            // Show ICD panel for the selected panel
            Inputs[ICDPanelIndex]?.fields?.forEach((field: any) => {
              if (field?.systemFieldName === 'ICDPanels') {
                field?.panels?.forEach((icdPanel: any) => {
                  if (icdPanel?.panelID === panel.panelID) {
                    icdPanel.isVisible = true;
                  }
                });
              }
            });
          }
        }
      });

      // Send only visible panels.
      const visibleToSend = panelsVisibleForSend(panelsToSend);
      if (visibleToSend.length > 0) {
        setPanelsArrToSend(visibleToSend);
      }
    }

    // CRITICAL: only selected panels — never all visible panels.
    // Assigning all visible panels + setInfectiousData after Continue remount
    // races with Screening clicks and auto-deselects them.
    const selectedVisible =
      panelsToSend.length > 0
        ? panelsVisibleForSend(panelsToSend)
        : panelsSelectedForSend(
            (currentField?.panels || []).filter((p: any) => p?.isSelected)
          );

    if (
      panelsToSend.length === 0 &&
      isPanelDefaultValueSynced(currentField, selectedVisible)
    ) {
      return;
    }

    const generation = ++updateGenerationRef.current;
    assignFormValues(
      Inputs,
      index,
      depControlIndex,
      fieldIndex,
      selectedVisible,
      isDependency,
      repeatFieldSection,
      isDependencyRepeatFields,
      repeatFieldIndex,
      repeatDependencySectionIndex,
      repeatDepFieldIndex,
      undefined,
      setInputs
    )?.then(updatedSections => {
      syncInfectiousSections(updatedSections, generation);
    });
  }, [props.IsSelectedByDefaultCompendiumData]);

  useEffect(() => {
    setpanelsArrToSendForEdit();
  }, [selectedPanelsKey]);

  const handleChangeParent = (
    panelID: number,
    panelName: string,
    checked: boolean,
    index: number
  ) => {
    props.fields.enableRule = '';
    const generation = ++updateGenerationRef.current;
    let panelsArrToAppendCopy = [...panelsArrToSend];
    const inputsCopy = JSON.parse(JSON.stringify(props?.Inputs));
    const ICDPanelIndex = getICDPanelsIndex(inputsCopy);

    if (checked) {
      const panel = inputsCopy[props.index].fields[props.fieldIndex].panels[index];
      if (panel.panelType === 'Screening') {
        inputsCopy[props.index].fields[props.fieldIndex].panels.forEach(
          (p: any, i: number) => {
            if (i !== index && p.panelType === 'Screening') {
              p.isSelected = false;
              p.testOptions.forEach((testOption: any) => {
                testOption.isSelected = false;
              });
              applyLivePanelSelection(i, false);
              panelsArrToAppendCopy = panelsArrItemRemoval(
                p.panelID,
                panelsArrToAppendCopy
              );
            }
          }
        );
        inputsCopy[ICDPanelIndex]?.fields?.forEach((i: any) => {
          if (i?.systemFieldName === 'ICDPanels') {
            i?.panels?.forEach((j: any) => {
              j.isVisible = false;
            });
          }
        });
      }

      inputsCopy[props.index].fields[props.fieldIndex].panels[index].isSelected =
        true;
      inputsCopy[props.index].fields[props.fieldIndex].panels[
        index
      ].testOptions.forEach((testOptions: any) => {
        testOptions.isSelected = true;
      });
      applyLivePanelSelection(index, true);

      const finalisedPanelsArrToSend = panelsArrMakerToSend(
        index,
        inputsCopy[props.index].fields[props.fieldIndex].panels[index],
        panelsArrToAppendCopy,
        checked
      );
      setPanelsArrToSend(panelsVisibleForSend(finalisedPanelsArrToSend));
      inputsCopy[ICDPanelIndex]?.fields?.forEach((i: any) => {
        if (i?.systemFieldName === 'ICDPanels') {
          i?.panels?.forEach((j: any) => {
            if (j?.panelID === panelID) {
              j.isVisible = true;
            }
          });
        }
      });
      assignFormValues(
        inputsCopy,
        props.index,
        props.depControlIndex,
        props.fieldIndex,
        panelsVisibleForSend(finalisedPanelsArrToSend),
        props.isDependency,
        props.repeatFieldSection,
        props.isDependencyRepeatFields,
        props.repeatFieldIndex,
        props.repeatDependencySectionIndex,
        props.repeatDepFieldIndex,
        undefined,
        props.setInputs
      )?.then(res => {
        syncInfectiousSections(res, generation);
      });
    } else {
      inputsCopy[props.index].fields[props.fieldIndex].panels[index].isSelected =
        false;
      inputsCopy[props.index].fields[props.fieldIndex].panels[
        index
      ].testOptions.forEach((testOptions: any) => {
        testOptions.isSelected = false;
      });
      applyLivePanelSelection(index, false);
      const finalisedPanelsArrToSend = panelsArrItemRemoval(
        panelID,
        panelsArrToAppendCopy
      );
      setPanelsArrToSend(panelsVisibleForSend(finalisedPanelsArrToSend));
      inputsCopy[ICDPanelIndex]?.fields?.forEach((i: any) => {
        if (i?.systemFieldName === 'ICDPanels') {
          i?.panels?.forEach((j: any) => {
            if (j?.panelID === panelID) {
              j.isVisible = false;
            }
          });
        }
      });
      assignFormValues(
        inputsCopy,
        props.index,
        props.depControlIndex,
        props.fieldIndex,
        panelsVisibleForSend(finalisedPanelsArrToSend),
        props.isDependency,
        props.repeatFieldSection,
        props.isDependencyRepeatFields,
        props.repeatFieldIndex,
        props.repeatDependencySectionIndex,
        props.repeatDepFieldIndex,
        undefined,
        props.setInputs
      )?.then(res => {
        syncInfectiousSections(res, generation);
      });
    }
  };

  const handleChangeChild = (
    testID: string,
    checked: boolean,
    parentPanelName: string,
    index: number,
    childIndex: number,
    panelID: any
  ) => {
    props.fields.enableRule = '';
    const generation = ++updateGenerationRef.current;
    const inputsCopy = JSON.parse(JSON.stringify(props.Inputs));
    let panelsArrToAppendCopy = JSON.parse(JSON.stringify(panelsArrToSend));
    let panelsCopy: any;
    if (props.panels) {
      panelsCopy = [...props.panels];
    }
    const ICDPanelIndex = getICDPanelsIndex(inputsCopy);
    let finalisedPanelsArrToSend = panelsArrToAppendCopy;

    if (checked) {
      const parentPanel =
        inputsCopy[props.index].fields[props.fieldIndex].panels[index];
      if (parentPanel.panelType === 'Screening') {
        inputsCopy[props.index].fields[props.fieldIndex].panels.forEach(
          (panel: any, i: number) => {
            if (i !== index && panel.panelType === 'Screening') {
              panel.isSelected = false;
              panel.testOptions.forEach((testOption: any) => {
                testOption.isSelected = false;
              });
              applyLivePanelSelection(i, false);
              panelsArrToAppendCopy = panelsArrItemRemoval(
                panel.panelID,
                panelsArrToAppendCopy
              );
            }
          }
        );
        inputsCopy[ICDPanelIndex]?.fields?.forEach((field: any) => {
          if (field?.systemFieldName === 'ICDPanels') {
            field?.panels?.forEach((icdPanel: any) => {
              if (icdPanel?.panelID !== panelID) {
                icdPanel.isVisible = false;
              }
            });
          }
        });
      }

      inputsCopy[props.index].fields[props.fieldIndex].panels[index].testOptions[
        childIndex
      ].isSelected = true;
      inputsCopy[props.index].fields[props.fieldIndex].panels[index].isSelected =
        true;
      // Update live UI immediately (parent + this child only)
      const livePanel =
        props.Inputs?.[props.index]?.fields?.[props.fieldIndex]?.panels?.[index];
      if (livePanel) {
        livePanel.isSelected = true;
        if (livePanel.testOptions?.[childIndex]) {
          livePanel.testOptions[childIndex].isSelected = true;
        }
      }

      finalisedPanelsArrToSend = panelsArrItemAddChild(
        index,
        parentPanelName,
        [
          inputsCopy[props.index].fields[props.fieldIndex].panels[index]
            .testOptions[childIndex],
        ],
        panelsArrToAppendCopy,
        panelsCopy
      );
      inputsCopy[ICDPanelIndex]?.fields?.forEach((field: any) => {
        if (field?.systemFieldName === 'ICDPanels') {
          field?.panels?.forEach((icdPanel: any) => {
            if (icdPanel?.panelID === panelID) {
              icdPanel.isVisible = true;
            }
          });
        }
      });
      setPanelsArrToSend(panelsVisibleForSend(finalisedPanelsArrToSend));
    } else {
      inputsCopy[props.index].fields[props.fieldIndex].panels[index].testOptions[
        childIndex
      ].isSelected = false;
      const livePanel =
        props.Inputs?.[props.index]?.fields?.[props.fieldIndex]?.panels?.[index];
      if (livePanel?.testOptions?.[childIndex]) {
        livePanel.testOptions[childIndex].isSelected = false;
      }
      const allChildUnchecked = inputsCopy[props.index].fields[
        props.fieldIndex
      ].panels[index].testOptions.every((option: any) => !option.isSelected);
      if (allChildUnchecked) {
        inputsCopy[props.index].fields[props.fieldIndex].panels[
          index
        ].isSelected = false;
        if (livePanel) livePanel.isSelected = false;
        inputsCopy[ICDPanelIndex]?.fields?.forEach((field: any) => {
          if (field?.systemFieldName === 'ICDPanels') {
            field?.panels?.forEach((icdPanel: any) => {
              if (icdPanel?.panelID === panelID) {
                icdPanel.isVisible = false;
              }
            });
          }
        });
      }
      finalisedPanelsArrToSend = removeTestFromSendArr(
        panelID,
        testID,
        panelsArrToAppendCopy
      );
      setPanelsArrToSend(panelsVisibleForSend(finalisedPanelsArrToSend));
    }

    assignFormValues(
      inputsCopy,
      props.index,
      props.depControlIndex,
      props.fieldIndex,
      panelsVisibleForSend(finalisedPanelsArrToSend),
      props.isDependency,
      props.repeatFieldSection,
      props.isDependencyRepeatFields,
      props.repeatFieldIndex,
      props.repeatDependencySectionIndex,
      props.repeatDepFieldIndex,
      undefined,
      props?.setInputs
    )?.then(res => {
      syncInfectiousSections(res, generation);
    });
  };

  const handleChangePanelType = (panelType: string, checked: boolean) => {
    props.fields.enableRule = '';
    const generation = ++updateGenerationRef.current;
    const inputsCopy = JSON.parse(JSON.stringify(props.Inputs));
    let panelsArrToAppendCopy = [...panelsArrToSend];
    const ICDPanelIndex = getICDPanelsIndex(inputsCopy);
    let finalisedPanelsArrToSend = panelsArrToAppendCopy;

    props.panels.forEach((panel: any, index: number) => {
      if (panel.panelType === panelType && panel.isVisible) {
        if (panelType === 'Screening' && checked) {
          props.panels.forEach((p: any, i: number) => {
            if (i !== index && p.panelType === 'Screening' && p.isSelected) {
              inputsCopy[props.index].fields[props.fieldIndex].panels[
                i
              ].isSelected = false;
              inputsCopy[props.index].fields[props.fieldIndex].panels[
                i
              ].testOptions.forEach((testOption: any) => {
                testOption.isSelected = false;
              });
              applyLivePanelSelection(i, false);
              panelsArrToAppendCopy = panelsArrItemRemoval(
                p.panelID,
                panelsArrToAppendCopy
              );
            }
          });
        }

        inputsCopy[props.index].fields[props.fieldIndex].panels[index].isSelected =
          checked;
        inputsCopy[props.index].fields[props.fieldIndex].panels[
          index
        ].testOptions.forEach((testOption: any) => {
          testOption.isSelected = checked;
        });
        applyLivePanelSelection(index, checked);

        if (checked) {
          finalisedPanelsArrToSend = panelsArrMakerToSend(
            index,
            inputsCopy[props.index].fields[props.fieldIndex].panels[index],
            panelsArrToAppendCopy,
            checked
          );
        } else {
          finalisedPanelsArrToSend = panelsArrItemRemoval(
            panel.panelID,
            panelsArrToAppendCopy
          );
        }
        panelsArrToAppendCopy = finalisedPanelsArrToSend;
        setPanelsArrToSend(panelsVisibleForSend(finalisedPanelsArrToSend));

        inputsCopy[ICDPanelIndex]?.fields?.forEach((i: any) => {
          if (i?.systemFieldName === 'ICDPanels') {
            i?.panels?.forEach((j: any) => {
              if (j?.panelID === panel.panelID) {
                j.isVisible = checked;
              }
            });
          }
        });
      }
    });

    assignFormValues(
      inputsCopy,
      props.index,
      props.depControlIndex,
      props.fieldIndex,
      panelsVisibleForSend(finalisedPanelsArrToSend),
      props.isDependency,
      props.repeatFieldSection,
      props.isDependencyRepeatFields,
      props.repeatFieldIndex,
      props.repeatDependencySectionIndex,
      props.repeatDepFieldIndex,
      undefined,
      props?.setInputs
    )?.then(res => {
      syncInfectiousSections(res, generation);
    });
  };

  const setpanelsArrToSendForEdit = () => {
    // Only the selected tests — otherwise tests unchecked after a whole-panel
    // select come back into the payload on the next selection change.
    const filteredPanelsCopy = panelsSelectedForSend(
      (props.panels || []).filter((panelsData: any) => panelsData?.isSelected)
    );
    const currentField =
      props.Inputs?.[props.index]?.fields?.[props.fieldIndex];

    setPanelsArrToSend(filteredPanelsCopy);

    // Skip when already in sync — prevents remount/Continue feedback loops.
    if (isPanelDefaultValueSynced(currentField, filteredPanelsCopy)) {
      return;
    }

    const generation = ++updateGenerationRef.current;
    const inputsCopy = props.Inputs ? [...props.Inputs] : [];
    assignFormValues(
      inputsCopy,
      props.index,
      props.depControlIndex,
      props.fieldIndex,
      filteredPanelsCopy,
      props.isDependency,
      props.repeatFieldSection,
      props.isDependencyRepeatFields,
      props.repeatFieldIndex,
      props.repeatDependencySectionIndex,
      props.repeatDepFieldIndex,
      undefined,
      props.setInputs
    )?.then(res => {
      syncInfectiousSections(res, generation);
    });
  };

  const divElement = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (props.error && divElement.current) {
      divElement.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
    if (props.setErrorFocussedInput) props.setErrorFocussedInput();
  }, [props.errorFocussedInput]);

  const groupedPanels = props.panels
    ?.filter((panel: any) => panel.isVisible)
    ?.reduce((acc: any, panel: any) => {
      const type = panel.panelType || 'Other';
      if (!acc[type]) {
        acc[type] = [];
      }
      acc[type].push(panel);
      return acc;
    }, {});

  const isPanelTypeSelected = (panelType: string) => {
    return groupedPanels[panelType]?.every((panel: any) => panel.isSelected);
  };

  return (
    <>
      {props.error && (
        <div className="form__error">
          <span>{t(props.error)}</span>
        </div>
      )}
      <div id={props.name} ref={divElement} tabIndex={-1}></div>

      {Object.keys(groupedPanels || {}).map((panelType: string) => (
        <div key={panelType} className="mb-4">
          <div className="card border border-gray-300 mb-3 rounded">
            <div className="card-header bg-light-primary text-white d-flex justify-content-between align-items-center rounded min-h-35px px-4">
              <label className="form-check form-check-sm form-check-solid">
                {panelType !== 'Screening' && (
                  <input
                    className="form-check-input mr-2 h-20px w-20px"
                    type="checkbox"
                    checked={isPanelTypeSelected(panelType)}
                    onChange={(e: any) => {
                      handleChangePanelType(panelType, e.target.checked);
                    }}
                  />
                )}
                <h5 className="m-0 d-inline">{panelType}</h5>
              </label>
            </div>
            <div className="card-body py-md-4 py-3">
              {groupedPanels[panelType].map((options: any) => (
                <div key={options.panelID} className="mb-3">
                  <div className="card border border-gray-300 mb-3 rounded">
                    <div
                      className="card-header bg-light d-flex justify-content-between align-items-center rounded min-h-35px px-4"
                      style={{ background: '#F3F6F9', borderRadius: '8px' }}
                    >
                      <div className="col-12">
                        <label className="form-check form-check-sm form-check-solid col-12">
                          <input
                            className="form-check-input mr-2 h-20px w-20px"
                            type="checkbox"
                            checked={!!options.isSelected}
                            onChange={(e: any) => {
                              handleChangeParent(
                                options.panelID,
                                options.panelName,
                                e.target.checked,
                                props.panels.findIndex(
                                  (p: any) => p.panelID === options.panelID
                                )
                              );
                            }}
                            disabled={
                              panelType === 'Screening' && props.screening
                            }
                          />
                          <span className="fw-600">{options.panelName}</span>
                        </label>
                      </div>
                    </div>
                    <div className="card-body py-md-4 py-3 col-12">
                      <div className="row">
                        {options.testOptions?.map(
                          (optionsChild: any, childIndex: any) =>
                            optionsChild.isVisible && (
                              <div
                                key={optionsChild.testID}
                                className="mb-3 col-xl-4 col-lg-4 col-md-4 col-sm-6"
                              >
                                <label className="form-check form-check-sm align-items-start form-check-solid col-12">
                                  <input
                                    className="form-check-input mr-2 h-20px w-20px"
                                    type="checkbox"
                                    onChange={(e: any) => {
                                      handleChangeChild(
                                        optionsChild?.testID,
                                        e?.target?.checked,
                                        options?.panelName,
                                        props.panels.findIndex(
                                          (p: any) =>
                                            p.panelID === options.panelID
                                        ),
                                        childIndex,
                                        options?.panelID
                                      );
                                    }}
                                    checked={
                                      !!options?.isSelected &&
                                      !!optionsChild?.isSelected
                                    }
                                    disabled={
                                      panelType === 'Screening' &&
                                      props.screening
                                    }
                                  />
                                  <span className="fw-400">
                                    {optionsChild.testName}
                                  </span>
                                </label>
                              </div>
                            )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      ))}
    </>
  );
};

export default ToxTestingOption;
 